import React from 'react';
import type { InstanceCardModel } from './InstanceCard';

interface InstancesAttentionSummaryProps {
  instances: InstanceCardModel[];
}

/**
 * Summary banner above the list. Answers "is anything wrong right now?"
 * before the operator scans cards. Severity escalates from info (only
 * running maintenance) through warning (attention) to danger (failed
 * maintenance). Stays absent when there's nothing to surface.
 */
export const InstancesAttentionSummary: React.FC<InstancesAttentionSummaryProps> = ({ instances }) => {
  const attention = instances.filter((i) => Boolean(i.attention));
  const failed = instances.filter((i) => i.maintenance?.status === 'failed');
  const running = instances.filter((i) => i.maintenance?.status === 'running');
  const outdated = instances.filter((i) => i.isOutdated && i.status !== 'deregistered' && i.status !== 'superseded');
  const stale = instances.filter((i) => i.status === 'stale');

  if (attention.length + failed.length + running.length === 0 && outdated.length === 0 && stale.length === 0) {
    return null;
  }

  const severity =
    failed.length > 0
      ? 'severity-danger'
      : attention.length > 0 || stale.length > 0
      ? 'severity-warning'
      : 'severity-info';

  const chips: Array<{ key: string; label: string }> = [];
  if (failed.length > 0) chips.push({ key: 'failed', label: `${failed.length} maintenance failed` });
  if (attention.length > 0) chips.push({ key: 'attention', label: `${attention.length} needing attention` });
  if (running.length > 0) chips.push({ key: 'running', label: `${running.length} maintenance running` });
  if (stale.length > 0) chips.push({ key: 'stale', label: `${stale.length} stale` });
  if (outdated.length > 0) chips.push({ key: 'outdated', label: `${outdated.length} on older versions` });

  const headline =
    failed.length > 0
      ? `${failed.length} installation${failed.length === 1 ? '' : 's'} report${failed.length === 1 ? 's' : ''} a failed maintenance run.`
      : attention.length > 0
      ? `${attention.length} installation${attention.length === 1 ? '' : 's'} need${attention.length === 1 ? 's' : ''} your attention.`
      : running.length > 0
      ? `${running.length} maintenance run${running.length === 1 ? ' is' : 's are'} currently in flight.`
      : stale.length > 0
      ? `${stale.length} installation${stale.length === 1 ? '' : 's'} ${stale.length === 1 ? 'has' : 'have'} a stale heartbeat.`
      : `${outdated.length} installation${outdated.length === 1 ? '' : 's'} ${outdated.length === 1 ? 'is' : 'are'} on older web part versions.`;

  return (
    <div className={`installations-attention ${severity}`} role="status">
      <span className="icon" aria-hidden="true">
        {severity === 'severity-danger' ? '✕' : severity === 'severity-warning' ? '⚠️' : 'ℹ️'}
      </span>
      <div className="text">
        <strong>{headline}</strong>
        <div className="chip-list">
          {chips.map((chip) => (
            <span key={chip.key} className="chip">{chip.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InstancesAttentionSummary;
